import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import {
  Analytics360Response,
  VendorAnalyticsItem,
  CategoryAnalyticsItem,
  PaginatedResponse,
} from './types';

export interface AnalyticsParams {
  period?: 'mtd' | 'ytd' | '30d' | '90d' | 'custom' | string;
  start_date?: string;
  end_date?: string;
  organization_id?: string;
  page?: number;
  page_size?: number;
}

/**
 * Fetch Analytics 360 overview: key totals, extremes, top lists, and AI spending insights
 */
export function useAnalytics360(params: AnalyticsParams = { period: 'mtd' }) {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
  );

  return useQuery<Analytics360Response>({
    queryKey: ['analytics-360', cleanParams],
    queryFn: async () => {
      const res = await api.get<Analytics360Response>('/v2/analytics/360/', {
        params: cleanParams,
      });
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch full pageable vendor ranking
 */
export function useVendorAnalytics(params: AnalyticsParams = { period: 'mtd' }) {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
  );

  return useQuery<PaginatedResponse<VendorAnalyticsItem>>({
    queryKey: ['analytics-vendors', cleanParams],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<VendorAnalyticsItem> | VendorAnalyticsItem[]>(
        '/v2/analytics/vendors/',
        { params: cleanParams }
      );
      if (Array.isArray(res.data)) {
        return {
          results: res.data,
          pagination: {
            page: 1,
            page_size: res.data.length,
            total: res.data.length,
            has_next: false,
          },
        };
      }
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch tag-based spend categorization breakdown
 */
export function useCategoryAnalytics(params: AnalyticsParams = { period: 'mtd' }) {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
  );

  return useQuery<CategoryAnalyticsItem[]>({
    queryKey: ['analytics-categories', cleanParams],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<CategoryAnalyticsItem> | CategoryAnalyticsItem[]>(
        '/v2/analytics/categories/',
        { params: cleanParams }
      );
      if (Array.isArray(res.data)) return res.data;
      return res.data?.results || [];
    },
    staleTime: 60 * 1000,
  });
}
